import * as auth from "./auth.js";
import axios from "axios";

const config = window.pcaSettings;

function handleError(err) {
  if (err) console.error(err);
  throw err;
}

async function request(url, method, body) {
  const options = {
    method: method || "GET",
    headers: {
      Authorization: `Bearer ${await auth.getToken()}`,
    },
    mode: "cors",
  };

  if (body !== null) {
    options.body = JSON.stringify(body);
    options.headers["Content-Type"] = "application/json";
  }

  let response;
  try {
    console.debug("Request opts:", JSON.stringify(options, null, 4));
    response = await fetch(url, options);
  } catch (err) {
    return handleError(new Error("Network error"));
  }

  console.debug("Response:", response);

  if (response.status === 403 || response.status === 401) {
    return auth.redirectToLogin("Unathenticated.");
  }

  if (response.status === 409 || response.status === 304) {
    return {
      message: "No changes detected. Save confirmed.",
      status: "unchanged",
    };
  }

  if (response.status < 200 || response.status >= 300) {
    let bodyText = "";
    try {
      bodyText = await response.text();
    } catch (_) {
      // ignore
    }
    const message = `HTTP ${response.status} ${response.statusText}${bodyText ? ` - ${bodyText}` : ''}`;
    return handleError(new Error(message));
  }

  // Some endpoints may return 204 No Content or an empty body on success
  // which would cause response.json() to throw. Handle that gracefully by
  // returning an empty object when no body is present.
  const responseText = await response.text();
  if (!responseText) {
    return {};
  }
  try {
    return JSON.parse(responseText);
  } catch (err) {
    // Not valid JSON, just return the raw text
    return responseText;
  }
}

async function getRequest(resource, data) {
  const url = new URL(`${config.api.uri}/${resource}`);

  if (data != null) {
    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => url.searchParams.append(key, v));
      } else {
        url.searchParams.append(key, value);
      }
    });
  }

  return request(url.toString());
}

async function postRequest(resource, body) {
  const url = new URL(`${config.api.uri}/${resource}`);
  return request(url.toString(), "POST", body);
}

async function putRequest(resource, body) {
  const url = new URL(`${config.api.uri}/${resource}`);
  return request(url.toString(), "PUT", body);
}

async function deleteRequest(resource) {
  const url = new URL(`${config.api.uri}/${resource}`);
  return request(url.toString(), "DELETE");
}

export async function get(key) {
  return getRequest(`get/${key}`);
}

export async function head(key) {
  return getRequest(`head/${key}`);
}

export async function search(query) {
  return getRequest("search", query);
}

export async function list(params) {
  return getRequest("list", params);
}

export async function swap(key) {
  return putRequest(`swap/${key}`);
}

export async function entities(key) {
  return getRequest("entities");
}

export async function languages(key) {
  return getRequest("languages");
}

export async function genaiquery(filename, query) {
  return getRequest(`genaiquery`, {
    "filename": filename,
    "query": query
  } );
}

export async function genaiQuery(filename, query) {
  return genaiquery(filename, query);
}

export async function override(key, body) {
  return postRequest(`get/${key}`, body);
}

export async function updateCallMetadata(key, metadata, userEmail) {
  return putRequest(`update-metadata/${key}`, { metadata, user: userEmail });
}

export async function genairefresh(filename,template) {
  return getRequest("genai/refreshsummary", {
    "filename": filename,
    "template":template
  } );
}

export async function improveQuestion(text) {
  return getRequest("genai/improvequestion", { text });
}

export async function getParsedFileSummary(filename) {
  if (!filename) {
    console.warn("getParsedFileSummary called without filename");
    return null;
  }
  return get(`parsedFiles/${filename}.json`);
}

export async function getCallDetails(key) {
  return get(key);
}

export async function getEntities() {
  return entities();
}

export async function getAgentPerformance() {
  return getRequest("agentPerformance");
}

// Legacy helper used by old parts of the UI to create a campaign via the
// generic `/upload` endpoint.  Newer code should call `createCampaign` defined
// further below which targets the dedicated `/campaigns` API.
export async function createCampaignUpload(data) {
  return postRequest("upload", data);
}

// Legacy upload API previously posted files directly to the backend which could
// result in payload too large errors.  This function now mirrors
// `uploadRecordingS3` to always use the presigned S3 URL flow so any callers do
// not have to change.
export async function uploadRecording(file, metadata = {}) {
  return uploadRecordingS3(file, metadata);
}

export async function uploadRecordingS3(
  file,
  metadata = {},
  onProgress,
  uploadType = "recording"
) {
  const presigned = await presign(
    file.name,
    uploadType,
    metadata.campaign || ""
  );

  await axios.put(presigned.url, file, {
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    onUploadProgress: (evt) => {
      if (onProgress) {
        const percent = Math.round((evt.loaded * 100) / (evt.total || file.size));
        onProgress(percent);
      }
    },
  });

  return { message: "uploaded" };
}

export async function copyFiles(configBody) {
  return postCrossS3(configBody);
}

export async function presign(filename, uploadtype, q_id) {
  return getRequest("presign", {
    "filename": filename,
    "uploadtype": uploadtype,
    "q_id":q_id,
  } );
}

export async function validation (params) {
  console.log("Validation params: ", params)
  return postRequest("upload", params);
}

export async function updateQaScorecard(data) {
  console.log("Update data : ", data)
  return postRequest("upload", data);
}

export async function qa () {
  return getRequest("templates")
}

// Scorecard Upload Functions
export async function uploadScorecardFile(file, templateName, campaign) {
  try {
    // Step 1: Get presigned URL for template upload
    const presignResponse = await presign(file.name, 'template', '');

    if (!presignResponse.success) {
      throw new Error('Failed to get presigned URL');
    }

    // Step 2: Upload file to S3
    await axios.put(presignResponse.url, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream'
      }
    });

    // Step 3: Trigger extraction Lambda
    const extractResponse = await postRequest('templates/upload', {
      bucket: presignResponse.bucket,
      key: presignResponse.key,
      templateName,
      campaign: campaign || null
    });

    return extractResponse;

  } catch (err) {
    console.error('Upload scorecard file error:', err);
    throw err;
  }
}

export async function getSk (query) {
  try {
    return getRequest("templates", { campaign: query })
  } catch (error) {
    console.error("Delete item failed:", error);
  }

}

export async function deleteQaScorecard (pk, sk) {
  console.log("PK & SK : ", pk, sk)
  try {
    return deleteRequest(`templates?PK=${pk}&SK=${sk}`);
  } catch (error) {
    console.error("Delete item failed:", error);
  }
}

export async function getCampaignStats() {
  return getRequest("campaignStats");
}

export async function getCrossS3(bucket_name, region = 'us-east-1') {
  try {
    return getRequest(`cross-account-S3?bucket=${bucket_name}&region=${region}`);
  } catch (error) {
    console.error("Failed to fetch from External Bucket: ", error);
  }
}

export async function postCrossS3(body) {
  console.log("API body: ", body)
  try {
    return postRequest("cross-account-s3-copy", body);
  } catch (error) {
    console.error("Failed to copy from External Bucket: ", error);
  }
}

export async function getRegularUsers() {
  return getRequest("users");
}

export async function assignusers(data) {
  return postRequest("users", data);
}

// ===== New Template API Functions =====
export async function listTemplates() {
  return getRequest("templates");
}

export async function fetchTemplate(name) {
  return getRequest(`templates/${name}`);
}

export async function fetchOvrLogs(lastEvaluatedKey, limit, Filename=null, startTime=null, endTime=null) {
  return getRequest(`get/audit`, { lastEvaluatedKey, limit, Filename, startTime, endTime });
}

export async function createTemplateApi(data) {
  return postRequest("templates", data);
}

export async function deleteTemplateApi(name) {
  // Call the correct templates endpoint for deleting a single template
  // The backend expects the plural path "templates/{id}" so adjust the
  // request URL accordingly.
  return deleteRequest(`templates/${encodeURIComponent(name)}`);
}

export async function deleteTemplateQuestionApi(template, question) {
  const t = encodeURIComponent(template);
  const q = encodeURIComponent(question);
  return deleteRequest(`templates/${t}/questions/${q}`);
}

// ===== Campaign API Functions =====
export async function listCampaignsApi() {
  return getRequest("campaigns");
}

export async function fetchCampaign(name) {
  return getRequest(`campaigns/${encodeURIComponent(name)}`);
}

export async function createCampaignApi(data) {
  return postRequest("campaigns", data);
}

export async function updateCampaignTemplatesApi(name, body) {
  return putRequest(`campaigns/${encodeURIComponent(name)}/templates`, body);
}

export async function updateActiveState(name) {
  return putRequest(`campaigns/${name}`);
}

export async function deleteCampaignApi(name) {
  return deleteRequest(`campaigns/${encodeURIComponent(name)}`);
}

// ===== Additional helper aliases matching backend API =====
// These functions mirror the older API names for compatibility with
// other parts of the application or documentation. They simply wrap the
// existing implementations above.

// ----- Template APIs matching backend specification -----
export async function getAllTemplates() {
  return getRequest("templates");
}

export async function getTemplate(templateName) {
  return getRequest(`templates/${encodeURIComponent(templateName)}`);
}

export async function createTemplate(data) {
  return postRequest("templates", data);
}

export async function deleteTemplateQuestion(templateName, questionName) {
  return deleteRequest(
    `templates/${encodeURIComponent(templateName)}/questions/${encodeURIComponent(questionName)}`
  );
}

// ----- Campaign APIs -----
export async function getAllCampaigns() {
  return getRequest("campaigns");
}

export async function getCampaign(campaignName) {
  return getRequest(`campaigns/${encodeURIComponent(campaignName)}`);
}

export async function createCampaign(data) {
  return postRequest("campaigns", data);
}

export async function updateCampaignTemplates(
  campaignName,
  templateName,
  action
) {
  return putRequest(`campaigns/${encodeURIComponent(campaignName)}/templates`, {
    templateName,
    action,
  });
}

export async function deleteCampaign(campaignName) {
  return deleteRequest(`campaigns/${encodeURIComponent(campaignName)}`);
}

export async function triggerbulk() {
  return postRequest("triggerbulk");
}

export async function fetchUploadedCalls(campaign) {
  const body = campaign ? { campaign } : {};
  return postRequest("upload", body);
}

// Search and Saved Searches endpoints
export async function getSearchAggregations() {
  return getRequest("search/aggregations");
}

export async function getSavedSearches() {
  return getRequest("saved-searches");
}

export async function createSavedSearch(searchName, query) {
  return postRequest("saved-searches", { searchName, query });
}

export async function updateSavedSearch(searchId, searchName) {
  return putRequest(`saved-searches/${searchId}`, { searchName });
}

export async function deleteSavedSearch(searchId) {
  return deleteRequest(`saved-searches/${searchId}`);
}

export async function getJobs() {
  return getRequest("job-search");
}

export async function listJobs(params = {}) {
  return getRequest("jobs", params);
}

export async function getJobDetail(jobId, params = {}) {
  return getRequest(`jobs/${jobId}`, params);
}

export async function controlGenesysJob(jobId, action) {
  return postRequest('api/genesys/control', { jobId, action });
}

export async function markAssessed(key) {
  return postRequest(`mark-assessed/${key}`, {});
}

export async function unmarkAssessed(key) {
  return postRequest(`unmark-assessed/${key}`, {});
}

export async function createJobMetadata(jobId, campaign, template, uploadedBy, totalCalls) {
  return postRequest("create-job-metadata", {
    jobId,
    campaign,
    template,
    uploadedBy,
    totalCalls
  });
}

export async function singleGenesysUpload(campaign, scorecard, conversationId) {
  return postRequest("single-upload", {
    campaign,
    scorecard,
    conversationId
  });
}

export async function previewInteractionCalls(campaign, template, startDate = null, endDate = null, filters = null) {
  const body = {
    campaign,
    template
  };

  // Add date range if provided
  if (startDate && endDate) {
    body.startDate = startDate;
    body.endDate = endDate;
  }

  // Add filters if provided
  if (filters) {
    body.filters = filters;
  }

  return postRequest('api/preview', body);
}

export async function fetchInteractionCalls(campaign, template, startDate = null, endDate = null, filters = null, batchJobId = null) {
  const body = {
    campaign,
    template
  };

  // Add date range if provided
  if (startDate && endDate) {
    body.startDate = startDate;
    body.endDate = endDate;
  }

  // Add filters if provided
  if (filters) {
    body.filters = filters;
  }

  if (batchJobId) {
    body.batchJobId = batchJobId;
  }

  return postRequest('api', body);
}

// Add this to your ../api/api.js file
export const fetchGenesysFilters = async (type = 'all') => {;
  return getRequest(`api/filters?type=${type}`);
};

// ===== Search Optimization API Functions =====
export async function getAgents() {
  return getRequest("agents");
}

export async function getAssignedTo() {
  return getRequest("agents", { type: "assignedto" });
}

// ===== Knowledge Base File Management API Functions =====

// Template KB files
export async function addKbFileToTemplate(templateName, fileId, s3Key) {
  return postRequest(`templates/${encodeURIComponent(templateName)}/kb-files`, { fileId, s3Key });
}

export async function deleteKbFileFromTemplate(templateName, fileId) {
  return deleteRequest(`templates/${encodeURIComponent(templateName)}/kb-files/${encodeURIComponent(fileId)}`);
}

// Campaign KB files
export async function addKbFileToCampaign(campaignName, fileId, s3Key) {
  return postRequest(`campaigns/${encodeURIComponent(campaignName)}/kb-files`, { fileId, s3Key });
}

export async function deleteKbFileFromCampaign(campaignName, fileId) {
  return deleteRequest(`campaigns/${encodeURIComponent(campaignName)}/kb-files/${encodeURIComponent(fileId)}`);
}

/**
 * Fetches all active scheduled jobs from the database.
 */
export const listScheduledJobsApi = async () => {
  return getRequest("api/schedule");
};

/**
 * Creates a new scheduled job entry in DynamoDB and triggers 
 * the EventBridge rule creation via Lambda.
 */
export const createScheduledJobApi = async (payload) => {
  return postRequest("api/schedule", payload);
};

/**
 * Deletes a scheduled job and its corresponding EventBridge rule.
 */
export const deleteScheduledJobApi = async (jobId) => {
  return deleteRequest(`api/schedule/${jobId}`);

};

export const updateScheduledJobApi = async (jobId, payload) => {
  return putRequest(`api/schedule/${jobId}`, payload);
}

export const getTranscribeQuota = async () => {
  return getRequest("transcribe-quota");
};

export const getTranscribeQuotaJob = async (jobId, date) => {
  const params = date ? { date } : {};
  return getRequest(`transcribe-quota/job/${jobId}`, params);
};

export const resumeTranscribeQuota = async (payload) => {
  return postRequest("transcribe-quota/resume", payload);
};

export const denyTranscribeQuota = async (payload) => {
  return postRequest("transcribe-quota/deny", payload);
};

export const getTranscribeQuotaDay = async (date, startKey) => {
  const params = { date };
  if (startKey) params.startKey = startKey;
  return getRequest("transcribe-quota", params);
};

export const setDefaultQuotaLimit = async (defaultLimit) => {
  return postRequest("transcribe-quota/default-limit", { defaultLimit });
};